import { Fragment, useState } from 'react';
import {
  roundRating,
  scaleScoreToDisplayMax,
  SKILL_SCORE_MAX,
  SPARK_SCORE_MAX,
  STAT_DISPLAY_MAX,
  TIME_SCORE_MAX,
  type DeliveryRatingBreakdown,
} from '../../../../domain';
import { InfoPopupButton } from '../../components/atomic/buttons/info_popup_button/InfoPopupButton';
import agilityIcon from '../../components/atomic/icons/stat_icons/agility_icon.png';
import collectibleIcon from '../../components/atomic/icons/stat_icons/collectible_icon.png';
import speedIcon from '../../components/atomic/icons/stat_icons/speed_icon.png';
import { CluePopup, type ClueId } from '../clues/CluePopup';
import resultPlateImg from './result_plate.png';
import resultValueBackgroundImg from './result_value_background.png';
import { ResultValueLabel } from './ResultValueLabel';
import ratingPlateImg from './total_rating/rating_plate.png';
import ratingTextImg from './total_rating/rating_text.png';
import { RatingStars } from './total_rating/RatingStars';
import agilityTextImg from './texts/agility_text.svg';
import collectibleTextImg from './texts/collectible_text.svg';
import speedTextImg from './texts/speed_text.svg';
import './ResultStatsContent.css';

const EMPTY_RATING: DeliveryRatingBreakdown = {
  timeScore: 0,
  sparkScore: 0,
  skillScore: 0,
  totalScore: 0,
  isPerfect: false,
};

function formatScoreOutOf(score: number, max: number): string {
  return `${roundRating(score).toFixed(1).replace('.', ',')} из ${max}`;
}

function formatStatOutOf5(score: number, internalMax: number): string {
  return formatScoreOutOf(scaleScoreToDisplayMax(score, internalMax), STAT_DISPLAY_MAX);
}

const STAT_ROWS = [
  {
    id: 'speed' as const satisfies ClueId,
    scoreKey: 'timeScore' as const,
    internalMax: TIME_SCORE_MAX,
    plate: { top: 448, left: 111.5 },
    valueBackground: { top: 478.5195, left: 684.2324 },
    icon: {
      src: speedIcon,
      top: 490,
      left: 161,
      width: 58,
      height: 107,
    },
    info: { top: 519.9805, left: 481.3164 },
    label: {
      src: speedTextImg,
      top: 517.9297,
      left: 264.9854,
      width: 199,
      height: 56,
    },
    valueText: {
      top: 505.5195,
      left: 740.7754,
      width: 155,
      height: 70,
    },
  },
  {
    id: 'collectible' as const satisfies ClueId,
    scoreKey: 'sparkScore' as const,
    internalMax: SPARK_SCORE_MAX,
    plate: { top: 627.8516, left: 111.5 },
    valueBackground: { top: 658.3711, left: 684.2324 },
    icon: {
      src: collectibleIcon,
      top: 668,
      left: 155,
      width: 95,
      height: 107,
    },
    info: { top: 699.832, left: 481.3164 },
    label: {
      src: collectibleTextImg,
      top: 697.7813,
      left: 264.9854,
      width: 193,
      height: 56,
    },
    valueText: {
      top: 685.3711,
      left: 723.7754,
      width: 187,
      height: 74,
    },
  },
  {
    id: 'agility' as const satisfies ClueId,
    scoreKey: 'skillScore' as const,
    internalMax: SKILL_SCORE_MAX,
    plate: { top: 807.7031, left: 111.5 },
    valueBackground: { top: 838.2227, left: 684.2324 },
    icon: {
      src: agilityIcon,
      top: 840.9902,
      left: 146.161,
      width: 98,
      height: 105,
    },
    info: { top: 879.6836, left: 481.3164 },
    label: {
      src: agilityTextImg,
      top: 877.6328,
      left: 264.9854,
      width: 200,
      height: 47,
    },
    valueText: {
      top: 865.2227,
      left: 749.7754,
      width: 137,
      height: 74,
    },
  },
] as const;

type ResultStatsContentProps = {
  rating?: DeliveryRatingBreakdown | null;
};

/** Result stats plates (Figma Group 1321317894 / 141:8990). */
export function ResultStatsContent({ rating }: ResultStatsContentProps) {
  const [activeClueId, setActiveClueId] = useState<ClueId | null>(null);
  const scores = rating ?? EMPTY_RATING;

  const toggleClue = (clueId: ClueId) => {
    setActiveClueId((current) => (current === clueId ? null : clueId));
  };

  return (
    <>
      {STAT_ROWS.map((row) => (
        <Fragment key={row.id}>
          <img
            className="result-stats-content__plate"
            src={resultPlateImg}
            alt=""
            draggable={false}
            aria-hidden
            style={{ top: row.plate.top, left: row.plate.left }}
          />
          <img
            className="result-stats-content__icon"
            src={row.icon.src}
            alt=""
            draggable={false}
            aria-hidden
            style={{
              top: row.icon.top,
              left: row.icon.left,
              width: row.icon.width,
              height: row.icon.height,
            }}
          />
          <img
            className="result-stats-content__label"
            src={row.label.src}
            alt=""
            draggable={false}
            aria-hidden
            style={{
              top: row.label.top,
              left: row.label.left,
              width: row.label.width,
              height: row.label.height,
            }}
          />
          <InfoPopupButton
            left={row.info.left}
            top={row.info.top}
            onClick={() => toggleClue(row.id)}
            ariaLabel={
              activeClueId === row.id
                ? 'Скрыть подсказку'
                : 'Показать подсказку'
            }
          />
          <img
            className="result-stats-content__value-background"
            src={resultValueBackgroundImg}
            alt=""
            draggable={false}
            aria-hidden
            style={{
              top: row.valueBackground.top,
              left: row.valueBackground.left,
            }}
          />
          <ResultValueLabel
            label={formatStatOutOf5(scores[row.scoreKey], row.internalMax)}
            width={row.valueText.width}
            height={row.valueText.height}
            style={{
              top: row.valueText.top,
              left: row.valueText.left,
            }}
          />
        </Fragment>
      ))}
      <img
        className="result-stats-content__rating-plate"
        src={ratingPlateImg}
        alt=""
        draggable={false}
        aria-hidden
      />
      <img
        className="result-stats-content__rating-text"
        src={ratingTextImg}
        alt=""
        draggable={false}
        aria-hidden
      />
      <img
        className="result-stats-content__value-background"
        src={resultValueBackgroundImg}
        alt=""
        draggable={false}
        aria-hidden
        style={{ top: 1082.5547, left: 684.2324 }}
      />
      <ResultValueLabel
        label={formatScoreOutOf(scores.totalScore, STAT_DISPLAY_MAX)}
        width={174}
        height={73}
        style={{
          top: 1104.5547,
          left: 728.7754,
        }}
      />
      <RatingStars rating={scores.totalScore} />
      {activeClueId ? (
        <>
          <button
            type="button"
            className="clue-popup-dismiss"
            aria-label="Закрыть подсказку"
            onClick={() => setActiveClueId(null)}
          />
          <CluePopup clueId={activeClueId} />
        </>
      ) : null}
    </>
  );
}
